/**
 * Colour derivation for per-tenant branding (roadmap Phase 2 item 9).
 *
 * A tenant admin picks ONE colour in a free-text field. Everything the shell
 * needs is derived from it here, in pure functions, so the arithmetic can be
 * tested without rendering anything.
 *
 * ## The rule this module exists to enforce
 *
 * A tenant controls HUE. BOSS controls CONTRAST.
 *
 * The obvious implementation — paint the tenant's colour straight onto the
 * active nav label — hands a customer the ability to make their own product
 * unreadable by typing "#1a1a1a" into a settings field, on a screen they then
 * cannot navigate away from. Equally, refusing to brand the text at all leaves
 * a red-branded tenant looking at violet text inside a red box, which reads as
 * a rendering fault rather than as restraint.
 *
 * So text colour is derived: keep the tenant's hue and saturation, raise the
 * lightness until the result actually clears a contrast threshold against the
 * surface it sits on. Measured, not assumed — see softenForSurface.
 *
 * Translucent decoration (backgrounds, borders, glows) does not need this
 * treatment. At 10-20% alpha over a dark surface, any hue stays subtle and
 * nothing is being read, so those consume the raw colour as an RGB triplet.
 */

/** The darker end of the sidebar gradient, --boss-sidebar-to in index.css.
 *
 * Contrast is computed against the DARKER stop rather than an average: the
 * active nav item can sit anywhere along the gradient, and a floor that holds
 * at the worst point holds everywhere. */
export const SIDEBAR_SURFACE = '#141028';

/** WCAG AAA for normal-size text.
 *
 * AA (4.5) is the legal floor and it is the wrong number here. The colour this
 * replaces is text-violet-300 (#c4b5fd), which measures 10.03 against this
 * surface — the existing design is not close to the minimum, it is a long way
 * clear of it. Deriving to 4.5 would be "legible" and would visibly DARKEN the
 * active nav label for every branded tenant, so the feature would land looking
 * like a regression to anyone who had the sidebar open before and after.
 *
 * 7.0 keeps the pastel character and is a published threshold rather than a
 * number chosen because it looked nice. Branded tenants sit slightly more
 * saturated than the unbranded BOSS violet; that difference is deliberate and
 * is the cost of preserving the tenant's hue.
 */
export const MIN_CONTRAST = 7;

/**
 * Accept only what can be safely written into a CSS custom property.
 *
 * Deliberately the same shape as SAFE_COLOR in BrandingContext. The value
 * originates in a tenant admin's text field and ends up in the document's
 * inline style, so anything that is not plainly a hex colour is dropped rather
 * than escaped — the blast radius of a permissive pattern here is style
 * injection on every page of that tenant.
 *
 * @returns {string|null} lowercase 6-digit hex with leading #, or null
 */
export function normalizeHex(value) {
  if (typeof value !== 'string') return null;
  const raw = value.trim().toLowerCase();
  if (!/^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/.test(raw)) return null;
  const body = raw.slice(1);
  if (body.length === 3) {
    return `#${body[0]}${body[0]}${body[1]}${body[1]}${body[2]}${body[2]}`;
  }
  return `#${body}`;
}

/**
 * "#8b5cf6" -> "139, 92, 246"
 *
 * A triplet rather than a colour so stylesheets can compose alpha with
 * rgba(var(--brand-primary-rgb), 0.2). That form degrades correctly: when the
 * variable is unset the whole declaration is invalid and the browser falls
 * back to the preceding rule, which is the existing violet. No JS branch and
 * no color-mix() support question.
 */
export function hexToRgbTriplet(value) {
  const hex = normalizeHex(value);
  if (!hex) return null;
  const n = parseInt(hex.slice(1), 16);
  /* eslint-disable no-bitwise */
  return `${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}`;
  /* eslint-enable no-bitwise */
}

// ─── contrast ──────────────────────────────────────────────────────────────

function toLinear(c) {
  const s = c / 255;
  return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
}

/** WCAG relative luminance. */
export function relativeLuminance(value) {
  const hex = normalizeHex(value);
  if (!hex) return null;
  const n = parseInt(hex.slice(1), 16);
  /* eslint-disable no-bitwise */
  const r = toLinear((n >> 16) & 255);
  const g = toLinear((n >> 8) & 255);
  const b = toLinear(n & 255);
  /* eslint-enable no-bitwise */
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** WCAG contrast ratio between two hex colours. 1 (identical) to 21. */
export function contrastRatio(a, b) {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  if (la === null || lb === null) return null;
  const [hi, lo] = la >= lb ? [la, lb] : [lb, la];
  return (hi + 0.05) / (lo + 0.05);
}

// ─── hsl ───────────────────────────────────────────────────────────────────

function hexToHsl(hex) {
  const n = parseInt(hex.slice(1), 16);
  /* eslint-disable no-bitwise */
  const r = ((n >> 16) & 255) / 255;
  const g = ((n >> 8) & 255) / 255;
  const b = (n & 255) / 255;
  /* eslint-enable no-bitwise */
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (max === min) return { h: 0, s: 0, l };
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h;
  if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
  else if (max === g) h = ((b - r) / d + 2) / 6;
  else h = ((r - g) / d + 4) / 6;
  return { h, s, l };
}

function hueToRgb(p, q, t0) {
  let t = t0;
  if (t < 0) t += 1;
  if (t > 1) t -= 1;
  if (t < 1 / 6) return p + (q - p) * 6 * t;
  if (t < 1 / 2) return q;
  if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
  return p;
}

function hslToHex({ h, s, l }) {
  let r;
  let g;
  let b;
  if (s === 0) {
    r = l;
    g = l;
    b = l;
  } else {
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hueToRgb(p, q, h + 1 / 3);
    g = hueToRgb(p, q, h);
    b = hueToRgb(p, q, h - 1 / 3);
  }
  const hex = (x) =>
    Math.round(x * 255)
      .toString(16)
      .padStart(2, '0');
  return `#${hex(r)}${hex(g)}${hex(b)}`;
}

// ─── the one that matters ──────────────────────────────────────────────────

/**
 * Lighten a tenant's colour until it is legible on a dark surface.
 *
 * Hue and saturation are preserved, so the result still reads as the tenant's
 * colour. Only lightness moves, and it moves by MEASUREMENT rather than by a
 * fixed target: lightness is raised one percentage point at a time until the
 * computed WCAG ratio against `surface` clears `minRatio`.
 *
 * A fixed lightness floor was the first approach and it is wrong. Perceived
 * brightness is not uniform across hues — at HSL lightness 78%, yellow clears
 * 4.5:1 against this sidebar comfortably while blue does not. Picking one
 * number means picking the hue it happens to suit and quietly failing the
 * others. Stepping until the measurement passes has no such blind spot.
 *
 * Never darkens. A tenant whose colour already clears the threshold gets it
 * back untouched, so the common case of a light brand colour is exact rather
 * than approximated.
 *
 * Terminates: lightness is capped at 1.0, and white clears 4.5:1 against any
 * surface dark enough to be a sidebar. The loop is bounded at 101 iterations
 * regardless, so a surface light enough to make the threshold unreachable
 * returns white rather than hanging.
 *
 * @returns {string|null} a 6-digit hex, or null if `value` is not a hex colour
 */
export function softenForSurface(
  value,
  { surface = SIDEBAR_SURFACE, minRatio = MIN_CONTRAST } = {},
) {
  const hex = normalizeHex(value);
  if (!hex) return null;
  if (contrastRatio(hex, surface) >= minRatio) return hex;

  const { h, s, l } = hexToHsl(hex);
  for (let step = Math.round(l * 100) + 1; step <= 100; step += 1) {
    const candidate = hslToHex({ h, s, l: step / 100 });
    if (contrastRatio(candidate, surface) >= minRatio) return candidate;
  }
  return '#ffffff';
}
